import { useEffect, useState } from "react";
import { api } from "../../lib/api";

function Chip({ on, onClick, children, testId, tone }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`h-9 px-3.5 rounded-lg text-xs font-bold border tap-press ${on ? `${tone} text-white` : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"}`}>
      {children}
    </button>
  );
}

export function useHierarchy() {
  const [hier, setHier] = useState(null);
  useEffect(() => { api.get("/hierarchy").then((r) => setHier(r.data)).catch(() => setHier({ tiers: [], nodes: [], venues: [] })); }, []);
  return hier;
}

export function distributionLabel(t, hier, venues) {
  const nodes = (t.node_ids || []).map((id) => hier?.nodes?.find((n) => n.id === id)?.name).filter(Boolean);
  const vs = (t.venue_ids || []).map((id) => venues.find((v) => v.id === id)?.name).filter(Boolean);
  const all = [...nodes, ...vs];
  return all.length ? all.join(", ") : "All outlets";
}

export function DistributionPicker({ nodeIds, venueIds, onChange, venues, allowGlobal, hier }) {
  if (!hier) return null;
  const toggle = (key, id) => {
    const cur = key === "node_ids" ? nodeIds : venueIds;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    onChange(key === "node_ids" ? { node_ids: next, venue_ids: venueIds } : { node_ids: nodeIds, venue_ids: next });
  };
  const tiers = hier.tiers || [];
  const reach = new Set(venueIds);
  (hier.venues || []).forEach((v) => { if ((v.chain || []).some((c) => nodeIds.includes(c))) reach.add(v.id); });
  const isGlobal = nodeIds.length + venueIds.length === 0;
  return (
    <div className="space-y-3" data-testid="distribution-picker">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Distribution</p>
        <span data-testid="distribution-reach" className={`text-[11px] font-bold px-2 py-0.5 rounded ${isGlobal ? (allowGlobal ? "bg-slate-900 text-white" : "bg-red-50 text-red-700") : "bg-slate-100 text-slate-700"}`}>
          {isGlobal ? (allowGlobal ? "Global — every outlet" : "Pick at least one group or venue") : `Reaches ${reach.size} outlet${reach.size === 1 ? "" : "s"}`}
        </span>
      </div>
      {tiers.map((tier, ti) => {
        const nodes = hier.nodes.filter((n) => n.tier === ti);
        if (!nodes.length) return null;
        return (
          <div key={tier.key}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">{tier.label}s</p>
            <div className="flex flex-wrap gap-2">
              {nodes.map((n) => <Chip key={n.id} testId={`dist-node-${n.id}`} tone={ti === 0 && tiers.length === 2 ? "bg-indigo-600 border-indigo-600" : "bg-sky-600 border-sky-600"} on={nodeIds.includes(n.id)} onClick={() => toggle("node_ids", n.id)}>{n.name} <span className="opacity-60 font-semibold">{n.venue_count}</span></Chip>)}
            </div>
          </div>
        );
      })}
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Individual venues</p>
        <div className="flex flex-wrap gap-2">
          {venues.map((v) => <Chip key={v.id} testId={`dist-venue-${v.id}`} tone="bg-emerald-600 border-emerald-600" on={venueIds.includes(v.id)} onClick={() => toggle("venue_ids", v.id)}>{v.name}</Chip>)}
        </div>
      </div>
    </div>
  );
}
